// src/components/LoginModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import { customToast } from '../utils/toastUtils';
import { authService } from '../services/authService';
import googleIcon from '../assets/google.svg';
import TermsModel from './TermsModel';
import PrivacyModel from './PrivacyModel';

import "./LoginModel.css";

// Country codes for phone authentication
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' }
];

const LoginModel = ({ show, onClose, onLoginSuccess }) => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [authMode, setAuthMode] = useState('email');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    phoneNumber: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // OTP provider toggle: 'firebase' or 'msg91'
  const otpProvider = import.meta.env.VITE_OTP_PROVIDER || 'firebase';

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        displayName: '',
        phoneNumber: '',
        verificationCode: ''
      });
      setOtpSent(false);
      setIsForgotPassword(false);
      setResetEmailSent(false);
      setConfirmationResult(null);
      setIsLoading(false);
      setResendTimer(0);
    }
  }, [show]);

  // Resend OTP countdown timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [resendTimer]);

  // Helper functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAuthModeChange = (mode) => {
    setAuthMode(mode);
    setOtpSent(false);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      displayName: '',
      phoneNumber: '',
      verificationCode: ''
    });
  };

  const handleCountryCodeChange = (e) => {
    setSelectedCountryCode(e.target.value);
  };

  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    setFormData(prev => ({
      ...prev,
      password: '',
      confirmPassword: '',
      displayName: ''
    }));
  };

  // Listen for successful authentication
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      if (user && show) {
        // Trigger success callback
        if (onLoginSuccess) {
          onLoginSuccess(user);
        }
        // Close modal
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 100);
      }
    });

    return () => unsubscribe();
  }, [show, onLoginSuccess, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);

    try {
      let result;
      
      if (authMode === 'email') {
        if (isLoginMode) {
          result = await authService.loginUser(formData.email, formData.password);
          
          if (result.success) {
            customToast.success('Welcome back!');
            // Don't close here - let useEffect handle it
          } else {
            customToast.error(result.error);
          }
        } else {
          if (!formData.displayName?.trim()) {
            customToast.error('Please enter your full name');
            return;
          }
          if (formData.password !== formData.confirmPassword) {
            customToast.error('Passwords do not match');
            return;
          }
          
          result = await authService.registerUser(formData.email, formData.password, formData.displayName);
          
          if (result.success) {
            customToast.success('Account created successfully!');
            // Don't close here - let useEffect handle it
          } else {
            customToast.error(result.error);
          }
        }
      } else {
        // Handle mobile OTP
        if (!otpSent) {
          const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
          const fullPhone = selectedCountryCode + cleanPhone;
          
          let result;
          if (otpProvider === 'msg91') {
            result = await authService.sendOTP_MSG91(fullPhone);
          } else {
            result = await authService.sendOTP(fullPhone);
          }
          
          if (result.success) {
            if (otpProvider !== 'msg91') {
              setConfirmationResult(result.confirmationResult);
            }
            setOtpSent(true);
            setResendTimer(45);
            customToast.success('OTP sent to your phone!');
          } else {
            customToast.error(result.error || 'Failed to send OTP. Please try again.');
          }
        } else {
          if (!formData.verificationCode || formData.verificationCode.length !== 6) {
            customToast.error('Please enter the complete 6-digit code');
            return;
          }

          let result;
          if (otpProvider === 'msg91') {
            const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
            const fullPhone = selectedCountryCode + cleanPhone;
            result = await authService.verifyOTP_MSG91(fullPhone, formData.verificationCode);
          } else {
            result = await authService.verifyOTP(confirmationResult, formData.verificationCode);
          }
          
          if (result.success) {
            customToast.success('Welcome! Signed in successfully');
            // Auth state listener will handle the rest
          } else {
            customToast.error(result.error || 'Invalid OTP. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      customToast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fast social login
  const handleSocialLogin = async (provider) => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      const result = await authService.socialLogin(provider);
      if (result.success) {
        customToast.success(`Welcome! Signed in with ${provider}`);
        // Don't close here - let useEffect handle it
      } else {
        customToast.error(result.error);
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
      customToast.error('Social login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      customToast.error('Please enter your email address first');
      return;
    }

    setIsLoading(true);
    try {
      const result = await authService.resetPassword(formData.email);
      if (result.success) {
        setResetEmailSent(true);
        customToast.success('Password reset email sent! Check your inbox.');
      } else {
        customToast.error(result.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      customToast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const phonePlaceholder = useMemo(() => {
    switch (selectedCountryCode) {
      case '+91': return 'Enter 10-digit number';
      case '+1': return 'Enter US number';
      case '+44': return 'Enter UK number';
      default: return 'Enter phone number';
    }
  }, [selectedCountryCode]);

  const selectedCountry = useMemo(() => 
    COUNTRY_CODES.find(c => c.code === selectedCountryCode),
    [selectedCountryCode]
  );

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="absolute top-2 right-2 text-black w-8 h-8 min-w-[32px] min-h-[32px] flex items-center justify-center rounded-full transition-all duration-200 z-[10001] font-bold cursor-pointer text-lg"
          onClick={onClose}
          style={{ opacity: 1, visibility: 'visible', pointerEvents: 'auto' }}
        >
          ×
        </button>
        <div id="recaptcha-container"></div>
        
        <h2 className="modal-title">
          {isForgotPassword ? 'Forgot Password' : authMode === 'mobile' ? 'Sign In with Mobile' : (isLoginMode ? 'Sign In' : 'Create Account')}
        </h2>

        {/* Auth Mode Toggle - hide during forgot password */}
        {!isForgotPassword && <div className="auth-mode-toggle">
          <label className="toggle-option">
            <input
              type="radio"
              name="authMode"
              value="email"
              checked={authMode === 'email'}
              onChange={() => handleAuthModeChange('email')}
            />
            <span className="toggle-label">Email</span>
          </label>
          <label className="toggle-option">
            <input
              type="radio"
              name="authMode"
              value="mobile"
              checked={authMode === 'mobile'}
              onChange={() => handleAuthModeChange('mobile')}
            />
            <span className="toggle-label">Mobile</span>
          </label>
        </div>}

        {/* Forgot Password View */}
        {isForgotPassword && (
          <div className="forgot-password-view">
            {!resetEmailSent ? (
              <form onSubmit={handleForgotPassword}>
                <p className="forgot-password-desc">Enter your email address and we'll send you a link to reset your password.</p>
                <div className="input-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="modal-input"
                    required
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="loading-text">
                      <span className="spinner"></span>
                      Sending...
                    </span>
                  ) : (
                    'Send Recovery Link'
                  )}
                </button>
                <p className="modal-switch">
                  Remember your password?{' '}
                  <span onClick={() => { setIsForgotPassword(false); setFormData(prev => ({ ...prev, email: '' })); }} className="modal-switch-link">
                    Back to Sign In
                  </span>
                </p>
              </form>
            ) : (
              <div className="reset-email-success">
                <p>✅ Recovery link sent! Check your inbox and follow the instructions to reset your password.</p>
                <button
                  type="button"
                  className="modal-submit-btn"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setResetEmailSent(false);
                    setFormData(prev => ({ ...prev, email: '', password: '' }));
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        )}

        {/* Email Form */}
        {authMode === 'email' && !isForgotPassword && (
          <form onSubmit={handleSubmit}>
            {!isLoginMode && (
              <div className="input-group">
                <input
                  type="text"
                  name="displayName"
                  placeholder="Full Name"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  className="modal-input"
                  required
                />
              </div>
            )}
            
            <div className="input-group">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                className="modal-input"
                required
                autoComplete="email"
              />
            </div>
            
            <div className="input-group" style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password (min 6 characters)"
                value={formData.password}
                onChange={handleInputChange}
                className="modal-input"
                minLength="6"
                required
                autoComplete={isLoginMode ? "current-password" : "new-password"}
                style={{ paddingRight: '42px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                )}
              </button>
            </div>

            {/* Confirm Password - Only in signup mode */}
            {!isLoginMode && (
              <div className="input-group" style={{ position: 'relative' }}>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="modal-input"
                  minLength="6"
                  required
                  autoComplete="new-password"
                  style={{ paddingRight: '42px' }}
                />
                {formData.confirmPassword.length > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px',
                      pointerEvents: 'none'
                    }}
                  >
                    {formData.password && formData.confirmPassword === formData.password ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    )}
                  </span>
                )}
              </div>
            )}

            {/* Forgot Password Link - Only show in login mode */}
            {isLoginMode && (
              <div className="forgot-password-container">
                <button 
                  type="button" 
                  className="forgot-password-link"
                  onClick={() => { setIsForgotPassword(true); setFormData(prev => ({ ...prev, password: '' })); }}
                >
                  Forgot Password?
                </button>
              </div>
            )}
            
            <button 
              type="submit" 
              className="modal-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-text">
                  <span className="spinner"></span>
                  {isLoginMode ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLoginMode ? 'Sign In' : 'Create Account'
              )}
            </button>

            {!isLoginMode && (
              <p style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'center', margin: '4px 0 0', lineHeight: '1.3' }}>
                By creating an account, you agree to our{' '}
                <span onClick={() => setShowTermsModal(true)} style={{ color: '#1e5128', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}>Terms & Conditions</span>
                {' '}and{' '}
                <span onClick={() => setShowPrivacyModal(true)} style={{ color: '#1e5128', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>.
              </p>
            )}

            <p className="modal-switch">
              {isLoginMode ? "Don't have an account? " : "Already have an account? "}
              <span onClick={switchMode} className="modal-switch-link">
                {isLoginMode ? 'Sign Up' : 'Sign In'}
              </span>
            </p>
          </form>
        )}

        {/* Enhanced Mobile Form */}
        {authMode === 'mobile' && (
          <form onSubmit={handleSubmit}>
            {!otpSent ? (
              <>
                <div className="mobile-info">
                  <p>🔐 Secure OTP verification for instant access</p>
                </div>

                <div className="phone-section">
                  <label className="input-label">Mobile Number</label>
                  
                  <div className="enhanced-phone-container">
                    <div className="country-code-display">
                      <span className="flag">{selectedCountry?.flag}</span>
                      <span className="code">{selectedCountryCode}</span>
                      <select
                        value={selectedCountryCode}
                        onChange={handleCountryCodeChange}
                        className="country-dropdown"
                      >
                        {COUNTRY_CODES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code} {country.country}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="phone-input-wrapper">
                      <input
                        type="tel"
                        name="phoneNumber"
                        placeholder={phonePlaceholder}
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="enhanced-phone-input"
                        required
                      />
                    </div>
                  </div>
                  
                  {formData.phoneNumber && (
                    <div className="number-preview">
                      <span className="preview-label">Number:</span>
                      <span className="preview-number">
                        {selectedCountryCode} {formData.phoneNumber}
                      </span>
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="modal-submit-btn otp-btn"
                  disabled={isLoading || !formData.phoneNumber}
                >
                  {isLoading ? (
                    <span className="loading-text">
                      <span className="spinner"></span>
                      Sending OTP...
                    </span>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </>
            ) : (
              <div className="otp-verification-section">
                <div className="otp-header">
                  <div className="otp-icon">✅</div>
                  <h3>OTP Sent Successfully!</h3>
                  <p>Enter the 6-digit code sent to:</p>
                  <div className="sent-to-number">
                    {selectedCountry?.flag} {selectedCountryCode} {formData.phoneNumber}
                  </div>
                </div>

                <div className="otp-input-section">
                  <label className="input-label">Verification Code</label>
                  <input
                    type="text"
                    name="verificationCode"
                    placeholder="000000"
                    value={formData.verificationCode}
                    onChange={handleInputChange}
                    className="enhanced-otp-input"
                    maxLength="6"
                    required
                  />
                  <div className="otp-help">
                    {formData.verificationCode.length}/6 digits entered
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="modal-submit-btn verify-btn"
                  disabled={isLoading || formData.verificationCode.length !== 6}
                >
                  {isLoading ? (
                    <span className="loading-text">
                      <span className="spinner"></span>
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>

                <div className="otp-actions">
                  <button 
                    type="button" 
                    className="action-btn secondary"
                    onClick={() => {
                      setOtpSent(false);
                      setFormData(prev => ({ ...prev, verificationCode: '' }));
                      setConfirmationResult(null);
                    }}
                  >
                    ← Change Number
                  </button>
                  <button 
                    type="button" 
                    className="action-btn primary"
                    onClick={async () => {
                      setIsLoading(true);
                      const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
                      const fullPhone = selectedCountryCode + cleanPhone;
                      let result;
                      if (otpProvider === 'msg91') {
                        result = await authService.resendOTP_MSG91(fullPhone);
                      } else {
                        result = await authService.sendOTP(fullPhone);
                      }
                      if (result.success) {
                        if (otpProvider !== 'msg91') {
                          setConfirmationResult(result.confirmationResult);
                        }
                        setFormData(prev => ({ ...prev, verificationCode: '' }));
                        customToast.success('OTP resent successfully!');
                        setResendTimer(45);
                      } else {
                        customToast.error(result.error || 'Failed to resend OTP');
                      }
                      setIsLoading(false);
                    }}
                    disabled={isLoading || resendTimer > 0}
                  >
                    {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}

        {/* Social Login - Only show when not in OTP verification or forgot password */}
        {!isForgotPassword && (authMode === 'email' || !otpSent) && (
          <div className="social-section">
            <div className="divider">
              <span>or continue with</span>
            </div>
            
            <div className="social-grid">
              <button 
                type="button"
                className="social-btn google-full"
                onClick={() => handleSocialLogin('google')}
                disabled={isLoading}
              >
                <img src={googleIcon} alt="Google" className="social-icon" />
                <span>Continue with Google</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Terms & Privacy Modals */}
      <TermsModel show={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyModel show={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
    </div>
  );
};

export default LoginModel;
